import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Bell,
  Building2,
  CreditCard,
  Gem,
  LayoutDashboard,
  LogOut,
  Menu,
  Search,
  Settings,
  Shapes,
  Users,
  WalletCards,
  X,
} from "lucide-react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { LOGOUT } from "@/constants/testIds/auth";

const navigation = [
  { to: "/dashboard", label: "Übersicht", icon: LayoutDashboard, end: true },
  { to: "/dashboard/metallkurse", label: "Metallkurse", icon: Gem },
  { to: "/dashboard/symbole", label: "Symbole", icon: Shapes },
];

export default function DashboardShell({
  title,
  subtitle,
  children,
  actions = null,
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };
  const menu = (
    <>
      <p className="px-3 font-mono text-[9px] uppercase tracking-[0.24em] text-white/25">
        Dashboard
      </p>
      <div className="mt-3 space-y-1">
        {navigation.map((item) => (
          <NavItem key={item.to} {...item} close={() => setOpen(false)} />
        ))}
      </div>
      <p className="mt-9 px-3 font-mono text-[9px] uppercase tracking-[0.24em] text-white/25">
        Finanzen
      </p>
      <div className="mt-3 space-y-1">
        <NavItem
          to="/dashboard/konten"
          icon={WalletCards}
          label="Konten"
          close={() => setOpen(false)}
        />
        <NavItem
          to="/dashboard/zahlungen"
          icon={CreditCard}
          label="Zahlungen"
          close={() => setOpen(false)}
        />
        <NavItem
          to="/dashboard/kunden"
          icon={Users}
          label="Kunden"
          close={() => setOpen(false)}
        />
      </div>
      <p className="mt-9 px-3 font-mono text-[9px] uppercase tracking-[0.24em] text-white/25">
        Organisation
      </p>
      <div className="mt-3 space-y-1">
        <NavItem
          to="/dashboard/unternehmen"
          icon={Building2}
          label="Unternehmen"
          close={() => setOpen(false)}
        />
        <NavItem
          to="/dashboard/einstellungen"
          icon={Settings}
          label="Einstellungen"
          close={() => setOpen(false)}
        />
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#05060a] text-white">
      <AnimatePresence>
        {open && (
          <>
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm lg:hidden"
              aria-label="Menü schließen"
            />
            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              className="fixed inset-y-0 left-0 z-50 flex w-[84vw] max-w-xs flex-col border-r border-white/10 bg-[#07080d] lg:hidden"
            >
              <Logo close={() => setOpen(false)} />
              <nav className="flex-1 px-4 py-7">{menu}</nav>
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 border-t border-white/10 px-7 py-5 text-sm text-white/55"
              >
                <LogOut className="h-4 w-4" />
                Abmelden
              </button>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-white/10 bg-[#07080d] lg:flex">
        <Logo />
        <nav className="flex-1 px-4 py-7">{menu}</nav>
        <div className="border-t border-white/10 p-4">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="flex h-9 w-9 items-center justify-center bg-[#e01e37] font-display text-sm font-bold">
              CF
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">{user?.name}</p>
              <p className="truncate text-[11px] text-white/35">
                {user?.email}
              </p>
            </div>
            <button
              onClick={handleLogout}
              data-testid={LOGOUT.button}
              className="text-white/30 hover:text-white"
              aria-label="Abmelden"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-white/10 bg-[#05060a]/90 px-5 backdrop-blur-xl sm:px-8 lg:h-20 lg:px-10">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setOpen(true)}
              className="lg:hidden"
              aria-label="Menü"
            >
              <Menu className="h-5 w-5" />
            </button>
            <div>
              <p className="font-display text-lg font-bold">{title}</p>
              {subtitle && (
                <p className="hidden text-[11px] text-white/35 sm:block">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {actions}
            <button className="hidden h-10 items-center gap-2 border border-white/10 px-3 text-xs text-white/40 sm:flex">
              <Search className="h-4 w-4" />
              Suchen
            </button>
            <button className="relative flex h-10 w-10 items-center justify-center border border-white/10 text-white/45">
              <Bell className="h-4 w-4" />
              <span className="absolute right-2.5 top-2.5 h-1.5 w-1.5 rounded-full bg-[#e01e37]" />
            </button>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[1500px] px-5 py-8 sm:px-8 lg:px-10 lg:py-10">
          {children}
        </main>
      </div>
    </div>
  );
}

function Logo({ close }) {
  return (
    <div className="flex h-20 items-center justify-between border-b border-white/10 px-7">
      <span className="font-display text-2xl font-extrabold">
        Cash<span className="text-[#e01e37]">FUX</span>
      </span>
      {close && (
        <button onClick={close} aria-label="Menü schließen">
          <X className="h-5 w-5 text-white/50" />
        </button>
      )}
    </div>
  );
}
function NavItem({ icon: Icon, label, to, end, close }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={close}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 text-sm transition ${isActive ? "bg-white/[0.06] text-white" : "text-white/40 hover:bg-white/[0.035] hover:text-white"}`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={`h-4 w-4 ${isActive ? "text-[#e01e37]" : ""}`} />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
}
function StaticItem({ icon: Icon, label, badge }) {
  return (
    <button className="flex w-full items-center gap-3 px-3 py-2.5 text-sm text-white/40 transition hover:bg-white/[0.035] hover:text-white">
      <Icon className="h-4 w-4" />
      <span className="flex-1 text-left">{label}</span>
      {badge && (
        <span className="font-mono text-[9px] text-[#e01e37]">{badge}</span>
      )}
    </button>
  );
}
